import { useSubjectId, useSubjectMember } from '@healthblocks-io/core'
import useSWR from 'swr'

import { useIntegrationAxios } from '@/modules/healthblocks/hooks/useIntegrationAxios'

import type { HylyghtActivity } from '@healthblocks-io/core'

/**
 *  Hook that exposes 2 functionalities related to Hylyght:
 *  - Get Hylyght activity by id
 *  - Get the magic link to Hylyght athlete portal
 *
 * @param id Hylyght activity id
 * @param type  Hylyght activity type (e.g. 'questionnaire' | 'program')
 */
export const useHylyght = () => {
  const axios = useIntegrationAxios()
  const subjectId = useSubjectId()
  const subject = useSubjectMember()

  /**
   * Get Hylyght activity by id
   * This function is used to get the Hylyght activity by id, in order to refresh an expired Hylyght link
   * And it will update the hylyght activity in Healthblocks
   * @returns
   */
  const getHylyghtActivityById = async (id: string, type: HylyghtActivity['type']) => {
    if (!id || !type) {
      throw new Error('No id or type provided')
    }

    return axios
      .get<HylyghtActivity>(`/hylyght/${type}/${id}`, {
        params: {
          memberId: subjectId,
        },
        cache: false,
      })
      .then((response) => response.data)
  }

  /**
   * Get the magic link to Hylyght athlete portal
   * @returns The magic link to the Hylyght athlete portal
   */
  const hylyghtAthleteMagicLinkFetcher = async () => {
    if (!subject?.meta.hylyghtAthleteId) return

    return axios
      .get<{
        link: string
        expires: number
      }>(`/hylyght/athlete-magic-link`, {
        params: {
          memberId: subjectId,
        },
        cache: false,
      })
      .then((response) => response.data)
  }

  const getHylyghtAthleteMagicLink = useSWR('hylyght-magic-link-' + subjectId, hylyghtAthleteMagicLinkFetcher, {
    errorRetryCount: 3,
  })

  return {
    getHylyghtActivityById,
    getHylyghtAthleteMagicLink,
  }
}
